import { cloneDeep } from "lodash";

import { parseResource, WorkspaceWithLicense } from "@examspark/api";
import { parseAwsJson } from "@examspark/common";
import {
  AnyObject,
  App,
  AppConfig,
  AppDomain,
  AppLicense,
  ParsedApp,
  User,
  Workspace,
  WorkspaceUser,
} from "@examspark/types";

export interface AppState<
  TAppLicenseConfig = AnyObject,
  TAppUserConfig = AnyObject,
> {
  /**
   * @todo This record is retrieved as a `Resource`, but we are typing it as a
   * `ParsedApp` to enable typesafe app configuration. We need to create a `getApp`
   * resolver so we can get this record as a proper `App`, but unsure of the
   * knockon effects. Punting. */
  app?: ParsedApp;
  appLicense?: AppLicense;
  appDomain?: AppDomain;
  proxiedApp?: ParsedApp;
  user?: User;
  sso?: boolean;
  userPrefs?: UserPrefs;
  workspaceUser?: WorkspaceUser;
  workspace?: Workspace;
  appLicenseConfig?: TAppLicenseConfig;
  appUserConfig?: TAppUserConfig;
  useSystemAdmin?: true;
  systemStatus?: "updated";
  version: number;
}

export type UserPrefs = Partial<{
  objectiveAuthorityIds: string[];
}>;

export type AppContextActions<
  TAppConfig = AppConfig,
  TAppLicenseConfig = AnyObject,
  TAppUserConfig = AnyObject,
> =
  | {
      type: "SET_APP";
      data: App | null;
    }
  | {
      type: "SET_APP_DOMAIN";
      data: AppDomain;
    }
  | {
      type: "APP_CONTEXT_SET_PROXIED_APP";
      data: App | null;
    }
  | {
      type: "SET_WORKSPACE";
      data: {
        workspace: Workspace;
        workspaceUser: WorkspaceUser | undefined;
        appLicense: AppLicense | undefined;
        app?: {
          id?: string;
        };
      };
    }
  | {
      type: "SELECT_WORKSPACE_WITH_LICENSE";
      data: WorkspaceWithLicense;
    }
  | {
      type: "SET_CONFIG";
      data: {
        appConfig?: TAppConfig;
        appLicenseConfig?: TAppLicenseConfig;
        appUserConfig?: TAppUserConfig;
      };
    }
  | { type: "CLEAR_WORKSPACE" }
  | {
      type: "SET_USER";
      data: User;
    }
  | {
      type: "SET_USER_SSO";
      data: {
        user?: User;
      };
    }
  | {
      type: "SET_USER_PREFS";
      data: UserPrefs;
    }
  | {
      type: "CLEAR";
    }
  | { type: "SET_SYSTEM_ADMIN" }
  | { type: "APP_UPDATED" };

export const appContextReducer = (
  state: AppState,
  action: AppContextActions,
) => {
  // console.log("AppContext", action);

  if (action.type === "SELECT_WORKSPACE_WITH_LICENSE") {
    action = {
      type: "SET_WORKSPACE",
      data: {
        workspace: action.data.workspace,
        appLicense: action.data.appLicense,
        workspaceUser: action.data,
      },
    };
  }

  switch (action.type) {
    case "SET_WORKSPACE": {
      const workspaceChanged = state.workspace?.id !== action.data.workspace.id;

      let actionData = action.data;

      if (action.data.app?.id) {
        if (state.app?.id === action.data.app.id) {
          // Don't clobber the app info if it hasn't changed
          actionData = cloneDeep(action.data);
          delete actionData.app;
        }
      }
      Object.assign(state, actionData);

      if (workspaceChanged) {
        delete state.appLicenseConfig;
        delete state.appUserConfig;
        delete state.useSystemAdmin;
      }
      break;
    }
    case "SET_CONFIG":
      //
      // Undefined in provided object should preserve existing value
      // @todo: it may be better to remove appConfig from this completely,
      // and change the contract to require both license and user config
      //
      if (action.data.appLicenseConfig !== undefined) {
        state.appLicenseConfig = action.data.appLicenseConfig;
      }
      if (action.data.appUserConfig !== undefined) {
        state.appUserConfig = action.data.appUserConfig;
      }
      break;
    case "CLEAR_WORKSPACE":
      delete state.workspaceUser;
      delete state.workspace;
      delete state.appLicense;
      delete state.appLicenseConfig;
      delete state.appUserConfig;
      break;
    case "CLEAR":
      for (const key of Object.keys(state) as Array<keyof AppState>) {
        if (["app", "proxiedApp", "appDomain"].includes(key)) {
          if (process.env.REACT_APP_EXAMSPARK_APP_ID != null) continue;
          if (state.appDomain) continue;
        }

        if (key === "version") continue;

        delete state[key];
      }
      break;
    case "SET_USER":
      state.user = action.data;
      break;
    case "SET_USER_SSO":
      state.user = action.data.user;
      state.sso = true;
      break;
    case "SET_USER_PREFS":
      state.userPrefs = action.data;
      break;
    case "SET_APP":
      if (action.data == null) {
        delete state.app;
        break;
      }
      state.app = parseResource(action.data, (r) => ({
        config: parseAwsJson<AppConfig>(r.config),
        serverConfig: undefined,
      }));
      break;
    case "SET_APP_DOMAIN":
      state.appDomain = action.data;
      break;
    case "APP_CONTEXT_SET_PROXIED_APP":
      if (action.data == null) {
        delete state.proxiedApp;
        break;
      }

      state.proxiedApp = parseResource(action.data, (r) => ({
        config: parseAwsJson<AppConfig>(r.config),
        serverConfig: undefined,
      }));

      break;
    case "SET_SYSTEM_ADMIN":
      delete state.workspaceUser;
      delete state.workspace;
      delete state.appLicense;
      delete state.appLicenseConfig;
      delete state.appUserConfig;
      state.useSystemAdmin = true;
      break;
    case "APP_UPDATED":
      state.systemStatus = "updated";
      break;
    default:
      action satisfies never;
  }

  state.version++;
};
